import html2canvas from 'html2canvas';

export class PNGExporter {
  /**
   * Export the resume card as a PNG image
   * @param {HTMLElement} element - The resume card DOM element to capture
   * @param {Object} options
   * @param {string} options.fileName - Download filename (default: 'runners-resume.png')
   * @param {string|null} options.shareUrl - Public share URL (reserved for future use)
   * @returns {Promise<HTMLCanvasElement>}
   */
  static async export(element, { fileName = 'runners-resume.png', shareUrl = null } = {}) {
    try {
      // 1. Wait for all fonts to load
      await document.fonts.ready;

      // 2. Wait for all images in the element to load
      await PNGExporter._waitForImages(element);

      // 3. Pre-convert cross-origin images to data URLs on the LIVE element
      //    (html2canvas onclone doesn't reliably handle async image conversion)
      const originalSrcs = await PNGExporter._convertImagesToDataURLs(element);

      // 4. Capture with html2canvas — use onclone to add export-mode and convert images
      const canvas = await html2canvas(element, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        onclone: async (clonedDoc) => {
          // Find the cloned element by ID
          const clonedEl = clonedDoc.getElementById(element.id) || clonedDoc.querySelector('.tpl-classic, .tpl-magazine');
          if (!clonedEl) return;

          // Add export-mode class to clone only (not live DOM)
          clonedEl.classList.add('export-mode');

          // Convert ALL cross-origin images to data URLs for html2canvas compatibility
          const images = clonedDoc.querySelectorAll('img');
          await Promise.all(Array.from(images).map(async (img) => {
            try {
              if (img.src && (img.src.startsWith('http') && !img.src.startsWith(window.location.origin))) {
                const response = await fetch(img.src, { mode: 'cors' });
                const blob = await response.blob();
                const dataUrl = await new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result);
                  reader.readAsDataURL(blob);
                });
                img.src = dataUrl;
              }
            } catch (e) {
              // Silently ignore — image will render as-is
            }
          }));
        },
      });

      // 5. Restore original image srcs on live element
      PNGExporter._restoreImageSrcs(element, originalSrcs);

      // 6. Trigger download
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = fileName;
      link.click();

      return canvas;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Wait for all images within an element to load
   * @param {HTMLElement} element
   * @returns {Promise<void>}
   */
  static async _waitForImages(element) {
    const images = element.querySelectorAll('img');
    const promises = Array.from(images).map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve; // don't block on failed images
      });
    });
    await Promise.all(promises);
  }

  /**
   * Convert cross-origin images to inline data URLs on the live element.
   * Returns a map of img → originalSrc so they can be restored after capture.
   */
  static async _convertImagesToDataURLs(element) {
    const images = element.querySelectorAll('img');
    const originalSrcs = new Map();

    await Promise.all(Array.from(images).map(async (img) => {
      if (!img.src || !img.src.startsWith('http')) return;
      // Skip same-origin images
      try {
        const imgUrl = new URL(img.src);
        if (imgUrl.origin === window.location.origin) return;
      } catch { return; }

      try {
        originalSrcs.set(img, img.src);
        const response = await fetch(img.src, { mode: 'cors' });
        const blob = await response.blob();
        const dataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        img.src = dataUrl;
      } catch {
        // Can't convert — remove from map so we don't try to restore
        originalSrcs.delete(img);
      }
    }));

    return originalSrcs;
  }

  /**
   * Restore original image srcs after capture.
   */
  static _restoreImageSrcs(element, originalSrcs) {
    for (const [img, src] of originalSrcs) {
      img.src = src;
    }
  }
}
